// BUILD-046 Owner-requested layout revision (visual edits ledger, Layout-2) and increment 1b-ii (Part B): the ONE toolbar of every tab.
//
// ONE wrapping row, always in this order:  Search | Status | Assigned to | Filter | Sort | (a reserved Presets slot) | Refresh (Assigned by Me only).
//   Search : about 256 px wide (w-64), no character limit (the input has no maxLength). Narrower than the row, it never pushes the page wider (max-w-full).
//   Status / Filter / Sort : outlined buttons. A control that does not apply to a tab is NOT drawn (it is not disabled and mysterious): Status exists where the tab has status choices, Sort where
//            it can sort, Filter where it has filters. A Filter popover with nothing in it is never drawn.
//   Presets slot : BUILD-046 section D fills the reserved room with the Presets control (PresetsMenu). Where a tab has none the room stays empty: no control, no text, not focusable, hidden from assistive technology.
//   Assigned to : the searchable picker (Company, Team, Assigned by Me), handed over by the page as one node. The active-filter badges are a row of their own below the toolbar (ActiveFiltersRow.tsx).
// There is NO "View" menu any more. The Comfortable / Compact choice is two icon-only buttons at the right end of the tab row (DensityButtons.tsx). "Show requester" is in the Filter popover under a "Display"
// section, offered exactly where it did its work before (Compact, never Assigned by Me). Every dropdown is a real button (aria-haspopup, aria-expanded, Escape closes, keyboard operable, visible
// focus) from DropdownMenu.tsx. The truthful partial-load sentence ("Searching the N loaded tasks; load more to search all.") sits under the row. This file draws; what each choice does (and every
// default) is the host's, exactly as before.
import type { ReactNode } from 'react';
import { DropdownMenu, FUNNEL_ICON, PopoverButton } from './DropdownMenu';
import type { DropdownItem } from './DropdownMenu';
import { FILTER_PANEL_LABEL_TASKS, describeToolbarRow } from './toolbarModel';

export interface ListToolbarProps {
  /** Name of the tab, for the group's accessible name. */
  scopeName: string;
  search: { value: string; label: string; placeholder: string; onChange: (next: string) => void };
  /** The status choices of the tab; null where the tab has none. `valueText` is the current choice ("Active"). */
  status: { valueText: string; items: readonly DropdownItem[] } | null;
  /** The tab's filters: `panel` holds the controls; `activeCount` is how many are set (0 shows no mark). Null where the tab has none. */
  filter: { activeCount: number; panel: ReactNode } | null;
  /** The Filter popover's accessible name: what it filters ("Filter tasks", "Filter requests" on Assigned by Me; toolbarModel.filterPanelLabel). */
  filterLabel?: string;
  /** The searchable Assigned to control (Company, Team, Assigned by Me), drawn between Status and Filter; null elsewhere. */
  assignee?: ReactNode;
  /** The sort modes of the tab; null where it cannot sort. `valueText` is the current sort's name ("Due Soonest"); the button reads "Sort: Due Soonest". */
  sort: { valueText: string; items: readonly DropdownItem[]; /** The whole order in words while sort rules apply (the button shows only "Priority +1"): the button's accessible name and a note inside the menu. */ fullText?: string | null } | null;
  /** The Presets control (BUILD-046 section D). Null: the reserved room stays empty. */
  presets?: ReactNode;
  /** One small control at the right end of the row (Assigned by Me: Refresh), drawn after the chips; it wraps with the row. Null draws nothing. */
  trailing?: ReactNode;
  /** The sentence that keeps a search truthful (viewSearchModel.searchScopeText); null while the query is blank. */
  searchScope: string | null;
  /** The sentence that keeps sorting truthful on a list that is not fully loaded (sortHeadingModel.sortScopeText); null otherwise. */
  sortScope?: string | null;
}

const MAGNIFIER = (
  <svg aria-hidden="true" viewBox="0 0 20 20" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="8.5" cy="8.5" r="5.25" />
    <path d="M12.5 12.5L17 17" />
  </svg>
);

export function ListToolbar({ scopeName, search, status, filter, filterLabel = FILTER_PANEL_LABEL_TASKS, assignee = null, sort, presets = null, trailing = null, searchScope, sortScope = null }: ListToolbarProps) {
  const drawn = describeToolbarRow({ status: status !== null, filter: filter !== null, display: false, sort: sort !== null });
  return (
    <div className="mb-3" data-testid="work-toolbar">
      <div role="group" aria-label={`${scopeName} list controls`} className="flex flex-wrap items-center gap-x-2 gap-y-2" data-testid="toolbar-row">
        <div className="relative w-64 max-w-full" data-testid="toolbar-search">
          {MAGNIFIER}
          <input
            type="search"
            value={search.value}
            onChange={(event) => search.onChange(event.target.value)}
            aria-label={search.label}
            placeholder={search.placeholder}
            className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
          />
        </div>
        {status !== null && drawn.includes('status') && <DropdownMenu testId="toolbar-status" buttonText={`Status: ${status.valueText}`} ariaLabel={`Status: ${status.valueText}`} menuLabel="Status" items={status.items} align="left" />}
        {assignee}
        {drawn.includes('filter') && (
          <PopoverButton
            testId="toolbar-filter"
            buttonText="Filter"
            ariaLabel={filter !== null && filter.activeCount > 0 ? `Filter, ${filter.activeCount} active` : 'Filter'}
            panelLabel={filterLabel}
            icon={FUNNEL_ICON}
            active={filter !== null && filter.activeCount > 0}
            align="right"
          >
            {filter !== null && filter.panel}
          </PopoverButton>
        )}
        {sort !== null && drawn.includes('sort') && <DropdownMenu testId="toolbar-sort" buttonText={`Sort: ${sort.valueText}`} ariaLabel={`Sort: ${sort.fullText ?? sort.valueText}`} title={sort.fullText ? `Sorted by ${sort.fullText}` : undefined} menuLabel="Sort by" menuNote={sort.fullText ? `Sorted by ${sort.fullText}` : null} items={sort.items} align="left" />}
        {/* The Presets slot (section D): the Presets control where the tab has one; otherwise the reserved room, empty, not focusable, hidden from assistive technology, drawn from the wide breakpoint up. */}
        {presets !== null ? <div data-testid="toolbar-presets-slot">{presets}</div> : <div aria-hidden="true" className="hidden xl:block xl:w-24" data-testid="toolbar-presets-slot" />}
        {trailing !== null && (
          <div className="ml-auto flex-shrink-0" data-testid="toolbar-trailing">
            {trailing}
          </div>
        )}
      </div>
      {searchScope !== null && (
        <p role="status" aria-live="polite" className="mt-2 text-xs text-gray-600" data-testid="search-scope">
          {searchScope}
        </p>
      )}
      {sortScope !== null && (
        <p role="status" aria-live="polite" className="mt-2 text-xs text-gray-600" data-testid="sort-scope">
          {sortScope}
        </p>
      )}
    </div>
  );
}
